// src/components/blog/features/BlogHeroSimple.tsx

'use client'

import Link from 'next/link'
import Image from 'next/image'
import { motion } from 'motion/react'
import { cn } from '~/lib/utils'
import type { BlogPost } from '~/lib/blog/types'
import { formatDate, formatDateTime } from '~/lib/utils/date'
import { DEFAULT_IMAGE } from '~/lib/blog/config'

interface BlogHeroProps {
  post: BlogPost
  className?: string
}

export function BlogHero({ post, className }: BlogHeroProps): JSX.Element {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className={cn(
        "group relative overflow-hidden rounded-2xl bg-gray-900",
        "h-full min-h-[520px]", // Fixed height that works with topics grid
        "shadow-md shadow-gray-950/50",
        className
      )}
    >
      {/* Image Container */}
      <div className="absolute inset-0">
        <div className="relative h-full w-full">
          <Image
            src={post.images.hero.url ?? post.images.preview.url ?? post.images.og.url ?? DEFAULT_IMAGE}
            alt={post.images.hero.alt ?? post.images.preview.alt ?? post.images.og.alt ?? post.title}
            className="object-cover transition-transform duration-700 will-change-transform group-hover:scale-105"
            sizes="(min-width: 1536px) 1280px, (min-width: 1280px) 1024px, (min-width: 1024px) 768px, 100vw"
            priority
            quality={90}
            fill
          />
          <div 
            className="absolute inset-0 bg-gradient-to-t from-gray-900 via-gray-900/80 to-transparent"
            aria-hidden="true"
          />
        </div>
      </div>

      {/* Content */}
      <div className="relative flex h-full flex-col justify-end p-6 sm:p-8 lg:p-10">
        <div className="max-w-3xl space-y-4">
          {post.topic && (
            <Link
              href={post.topic.href}
              className="inline-flex items-center gap-2 rounded-full bg-blue-500/20 px-4 py-1.5 text-sm font-medium text-blue-200 transition-colors hover:bg-blue-500/30 hover:text-blue-100 backdrop-blur-sm"
            >
              {post.topic.title}
            </Link>
          )}
          <Link href={post.href} className="group/link block space-y-3">
            <h2 className="text-2xl font-bold text-white transition-colors group-hover/link:text-blue-200 sm:text-3xl lg:text-4xl">
              {post.title}
            </h2>
            <p className="text-base text-gray-200 line-clamp-2 sm:text-lg">
              {post.description}
            </p>
          </Link>
          <div className="flex flex-col gap-4 pt-4 sm:flex-row sm:items-center sm:justify-between">
            <Link 
              href={post.author.href}
              className="flex items-center gap-3 text-gray-300 transition-colors hover:text-white"
            >
              <div className="relative h-10 w-10 overflow-hidden rounded-full ring-2 ring-white/20">
                <Image
                  src={post.author.imageUrl}
                  alt={post.author.name}
                  className="object-cover"
                  fill
                  sizes="40px"
                />
              </div>
              <div>
                <span className="block text-sm font-medium sm:text-base">
                  {post.author.name}
                </span>
                {post.author.role && (
                  <span className="mt-0.5 block text-sm text-gray-400">
                    {post.author.role}
                  </span>
                )}
              </div>
            </Link>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <time dateTime={formatDateTime(post.datetime)}>
                {formatDate(post.date, 'full')}
              </time>
              <span aria-hidden="true">&middot;</span>
              <span>{post.readingTime} min read</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  )
} 